import {
    Desk,
    DeskRef,
    Employee,
    EmployeeRef,
    Floor,
    INeighbourhood,
    Neighbourhood,
    SeatingPlan,
    SeatingPlanDeskAssignment,
    SeatingPlanMove,
    SeatingPlanNeighbourhoodRef,
    SeatingPlanNewNeighbourhood,
    SeatingPlanNewNeighbourhoodForm,
    UNASSIGNED_NEIGHBOURHOOD_KEY,
} from "../../api/Model";
import {EmployeeVisitSheet} from "../../api/EmployeeVisitStore";
import {associateBy, getOrSet, groupBy, invertMapToGroups, mapValues} from "../../util/MapUtil";
import {EmployeeNeighbourhood} from "../../api/EmployeeNeighbourhoodStore";
import {AssignedDesks} from "../../api/AssignedDeskStore";
import {emptyArray, idFn, keyFn} from "../../util/FunctionUtil";
import {Opt} from "../../util/Optional";
import {mapNotNull} from "../../util/ArrayUtil";
import {SeatingPlanStore} from "../../api/SeatingPlanStore";
import {toDeskRef} from "../../api/Projections";

export type HoodItem = INeighbourhood & {
    oldEmployees: EmployeeRef[],
    newEmployees: EmployeeRef[],
    oldDesks: DeskRef[],
    newDesks: DeskRef[],
    originalProps?: SeatingPlanNewNeighbourhoodForm,
};

export class SeatingPlanModel {

    private constructor(
        public readonly plan: SeatingPlan,
        public readonly hoodItems: HoodItem[],
        public readonly assignableHoodItems: HoodItem[],
        public readonly desks: Desk[],
        public readonly floors: Floor[],
        public readonly employeeVisits: EmployeeVisitSheet,
        public readonly assignedDesks: AssignedDesks,
        private readonly deskAssignments: Map<Desk['id'], {
            old: {
                employee: Opt<EmployeeRef>,
                neighbourhood: Opt<HoodItem>,
            },
            new?: {
                employee: Opt<EmployeeRef>,
                neighbourhood: Opt<HoodItem>,
            },
        }>,
        readonly hoodKey2employeeId2move: Map<INeighbourhood['key'], Map<Employee['id'], SeatingPlanMove>>,
        readonly employeeId2moves: Map<Employee['id'], SeatingPlanMove[]>,
        readonly originalEmployeeNeighbourhood: EmployeeNeighbourhood<HoodItem>,
        readonly modifiedEmployeeNeighbourhood: EmployeeNeighbourhood<HoodItem>,
        private readonly store: SeatingPlanStore,
    ) {
    }

    isMoved(item: { employee?: EmployeeRef, hood?: HoodItem }): boolean {
        if (item.hood) {
            const employeeId2moves = this.hoodKey2employeeId2move.get(item.hood.key);
            if (employeeId2moves === undefined) {
                return false;
            }
            if (item.employee) {
                return employeeId2moves.has(item.employee.id);
            } else {
                return true;
            }
        } else if (item.employee) {
            return this.employeeId2moves.has(item.employee.id);
        } else {
            return false;
        }
    }

    countMovedEmployees(): number {
        return this.employeeId2moves.size;
    }

    private movesForEmployee(employee: EmployeeRef, prevHoods: INeighbourhood[], newHoods: INeighbourhood[]) {
        const prevHoodKeys = new Set(prevHoods.map(hood => hood.key));
        const newHoodKeys = new Set(newHoods.map(hood => hood.key));

        const newMoves: SeatingPlanMove[] = [];
        this.assignableHoodItems.forEach(hood => {
            if (!prevHoodKeys.has(hood.key) && newHoodKeys.has(hood.key)) {
                newMoves.push({
                    employee,
                    neighbourhood: SeatingPlanModel.hoodRef(hood),
                    operation: 'add',
                });
            }
            if (prevHoodKeys.has(hood.key) && !newHoodKeys.has(hood.key)) {
                newMoves.push({
                    employee,
                    neighbourhood: SeatingPlanModel.hoodRef(hood),
                    operation: 'remove',
                });
            }
        });
        return newMoves;
    }

    async moveEmployee(employee: Employee, newHoods: INeighbourhood[]) {
        const employeeRef: EmployeeRef = {
            id: employee.id,
            fullName: employee.fullName,
            deactivated: employee.deactivated
        };
        const oldHoods = this.originalEmployeeNeighbourhood.get(employee);
        const newMoves = this.movesForEmployee(employeeRef, oldHoods, newHoods);
        await this.store.setEmployeeMoves(this.plan, employeeRef, newMoves);
    }

    async revertEmployeeMove(move: SeatingPlanMove) {
        if (SeatingPlanModel.hoodRefKey(move.neighbourhood) === UNASSIGNED_NEIGHBOURHOOD_KEY) {
            await this.store.setEmployeeMoves(this.plan, move.employee, []);
        } else {
            await this.store.setEmployeeMoves(
                this.plan,
                move.employee,
                (this.employeeId2moves.get(move.employee.id) ?? [])
                    .filter(m =>
                        m.operation !== move.operation ||
                        SeatingPlanModel.hoodRefKey(m.neighbourhood) !== SeatingPlanModel.hoodRefKey(move.neighbourhood)
                    )
            );
        }
    }

    newDeskAssignment(desk: DeskRef): {
        neighbourhood: Opt<HoodItem>,
        employee: Opt<EmployeeRef>,
    } {
        const assignment = this.deskAssignments.get(desk.id);
        return assignment
            ? assignment.new ?? assignment.old
            : {neighbourhood: null, employee: null}
            ;
    }

    oldDeskAssignment(desk: DeskRef) {
        const assignment = this.deskAssignments.get(desk.id);
        return assignment
            ? assignment.old
            : {neighbourhood: null, employee: null}
            ;
    }

    isDeskModified(desk: DeskRef): boolean {
        const assignment = this.deskAssignments.get(desk.id)?.new;
        return assignment !== null && assignment !== undefined;
    }

    async assignDesk(desk: Desk, newNeighbourhood: Opt<HoodItem>, newEmployee: Opt<EmployeeRef>) {
        const oldAssignment = this.oldDeskAssignment(desk);
        await this.store.setDeskAssignment(this.plan, desk, (oldAssignment.neighbourhood?.key !== newNeighbourhood?.key || oldAssignment.employee?.id !== newEmployee?.id) ? {
            desk: toDeskRef(desk),
            neighbourhood: newNeighbourhood && SeatingPlanModel.hoodRef(newNeighbourhood),
            employee: newEmployee,
        } : undefined);
    }

    async revertDeskAssignment(desk: DeskRef) {
        await this.store.setDeskAssignment(this.plan, desk, undefined);
    }

    async setNeighbourhoodUpdate(neighbourhood: Neighbourhood, form: SeatingPlanNewNeighbourhoodForm | undefined) {
        if (form) {
            await this.store.updateNeighbourhoodUpdate(this.plan, neighbourhood, form);
        } else {
            await this.store.deleteNeighbourhoodUpdate(this.plan, neighbourhood);
        }
    }

    async revertNewNeighbourhood(neighbourhood: SeatingPlanNewNeighbourhood) {
        await this.store.deleteNewNeighbourhood(this.plan, neighbourhood);
    }

    static isEqualHood = (opt: HoodItem, val: HoodItem) => (
        opt.key === val.key
    );

    static hoodRefKey(hoodRef: SeatingPlanNeighbourhoodRef) {
        return `${hoodRef.type}-${hoodRef.id}`;
    }

    private static hoodRef(hood: INeighbourhood): SeatingPlanNeighbourhoodRef {
        return hood.type === 'new'
            ? {type: 'new', id: hood.id}
            : {type: 'existing', id: hood.id};
    }

    private static hoodEmployees(hoods: INeighbourhood[], moves: SeatingPlanMove[]): Map<HoodItem['key'], {
        oldEmployees: EmployeeRef[],
        newEmployees: EmployeeRef[]
    }> {
        const hoodKey2employees = new Map<HoodItem['key'], {
            oldEmployees: EmployeeRef[],
            newEmployees: Map<EmployeeRef['id'], EmployeeRef>
        }>(
            hoods.map(hood => {
                const oldEmployees = hood.type === 'new' ? [] : hood.employees;
                return [hood.key, {
                    oldEmployees,
                    newEmployees: associateBy(oldEmployees, it => it.id),
                }];
            })
        );

        for (const move of moves) {
            const hoodKey: HoodItem['key'] = SeatingPlanModel.hoodRefKey(move.neighbourhood);
            const hoodEmployees = hoodKey2employees.get(hoodKey);
            if (!hoodEmployees) continue;
            const {newEmployees} = hoodEmployees;

            switch (move.operation) {
                case "add":
                    newEmployees.set(move.employee.id, move.employee);
                    break;
                case "remove":
                    newEmployees.delete(move.employee.id);
                    break;
            }
        }

        const employeesWithNewHood = [...hoodKey2employees]
            .filter(([hoodKey]) => hoodKey !== UNASSIGNED_NEIGHBOURHOOD_KEY)
            .flatMap(([_, {newEmployees}]) => [...newEmployees.values()]);
        const residentsWithoutNewHood = associateBy(
            [...hoodKey2employees.values()].flatMap(it => it.oldEmployees),
            it => it.id,
        );
        employeesWithNewHood.forEach(employee => {
            residentsWithoutNewHood.delete(employee.id);
        });
        const unassignedHood = hoodKey2employees.get(UNASSIGNED_NEIGHBOURHOOD_KEY);
        if (unassignedHood) {
            unassignedHood.newEmployees = residentsWithoutNewHood;
        }

        return mapValues(
            hoodKey2employees,
            ({oldEmployees, newEmployees}) => ({
                oldEmployees,
                newEmployees: [...newEmployees.values()]
            })
        );
    }

    private static hoodDesks(
        hoods: INeighbourhood[],
        desks: Desk[],
        deskAssignments: SeatingPlanDeskAssignment[],
    ): Map<INeighbourhood['key'], {
        oldDesks: DeskRef[],
        newDesks: DeskRef[],
    }> {
        const oldHoodKeyByDeskId: Map<DeskRef['id'], INeighbourhood['key']> = associateBy(
            desks,
            desk => desk.id,
            desk => desk.neighbourhoodId
                ? SeatingPlanModel.hoodRefKey({type: 'existing', id: desk.neighbourhoodId})
                : UNASSIGNED_NEIGHBOURHOOD_KEY,
        );
        const newHoodKeyByDeskId = new Map(oldHoodKeyByDeskId);

        deskAssignments.forEach(deskMove => {
            newHoodKeyByDeskId.set(
                deskMove.desk.id,
                deskMove.neighbourhood ? SeatingPlanModel.hoodRefKey(deskMove.neighbourhood) : UNASSIGNED_NEIGHBOURHOOD_KEY,
            );
        });

        const desksById = associateBy(desks, idFn);
        const oldDesksByHoodKey = mapValues(
            invertMapToGroups(oldHoodKeyByDeskId),
            (deskIds) => mapNotNull(deskIds, id => desksById.get(id)),
        );
        const newDesksByHoodKey = mapValues(
            invertMapToGroups(newHoodKeyByDeskId),
            (deskIds) => mapNotNull(deskIds, id => desksById.get(id)),
        );

        return associateBy(
            hoods,
            hood => hood.key,
            hood => ({
                oldDesks: oldDesksByHoodKey.get(hood.key) || [],
                newDesks: newDesksByHoodKey.get(hood.key) || [],
            }),
        );
    }

    private static deskAssignments(
        desks: Desk[],
        hoods: HoodItem[],
        deskAssignments: SeatingPlanDeskAssignment[],
    ): SeatingPlanModel["deskAssignments"] {
        const hoodByKey = associateBy(hoods, keyFn);
        const newDeskAssignments = associateBy(
            deskAssignments,
            assignment => assignment.desk.id,
        );

        const assignments = desks
            .map<SeatingPlanDeskAssignment>(desk => {
                const oldHoodRef = desk.neighbourhoodId
                    ? SeatingPlanModel.hoodRefKey({type: 'existing', id: desk.neighbourhoodId})
                    : null;
                const newDeskAssignment = newDeskAssignments.get(desk.id);
                const newHoodRef = newDeskAssignment?.neighbourhood
                    ? SeatingPlanModel.hoodRefKey(newDeskAssignment.neighbourhood)
                    : null;

                const newHood = newHoodRef ? hoodByKey.get(newHoodRef) : null;
                const oldHood = oldHoodRef ? hoodByKey.get(oldHoodRef) : null;
                return ({
                    desk,
                    'old': {
                        neighbourhood: oldHood,
                        employee: desk.assignedTo,
                    },
                    'new': newDeskAssignment && (newHood?.key !== oldHood?.key || desk.assignedTo?.id !== newDeskAssignment?.employee?.id) ? {
                        neighbourhood: newHood,
                        employee: newDeskAssignment?.employee,
                    } : null,
                });
            });
        return associateBy(assignments, assignment => assignment.desk.id);
    }

    private static buildHoodItems(
        plan: SeatingPlan,
        existingNeighbourhoods: Neighbourhood[],
        desks: Desk[],
    ): HoodItem[] {
        const newNeighbourhoods = plan.newNeighbourhoods;
        const neighbourhoodUpdates = plan.neighbourhoodUpdates;
        const moves = plan.moves;
        const deskAssignments = plan.deskAssignments;

        const hoodPatches = associateBy(
            neighbourhoodUpdates,
            update => update.neighbourhoodId,
        );

        const hoods: INeighbourhood[] & { originalProps?: SeatingPlanNewNeighbourhoodForm } = [
            ...newNeighbourhoods.map(hood => {
                if (!hood.type) {
                    hood.type = 'new';
                }
                return hood;
            }),
            ...existingNeighbourhoods
                .map(hood => {
                    if (!hood.type) {
                        hood.type = 'existing';
                    }
                    return hood;
                })
                .map(existingHood => {
                    const hoodPatch = hoodPatches.get(existingHood.id);
                    return !hoodPatch ? (
                        existingHood
                    ) : ({
                        ...existingHood,
                        name: hoodPatch.name,
                        displayColor: hoodPatch.displayColor,
                        notes: hoodPatch.notes,
                        slackChannelId: hoodPatch.slackChannelId,
                        originalProps: {
                            name: existingHood.name,
                            displayColor: existingHood.displayColor,
                            notes: existingHood.notes,
                            slackChannelId: existingHood.slackChannelId,
                        },
                    });
                })
        ];

        const hoodKey2Employees = SeatingPlanModel.hoodEmployees(hoods, moves);
        const hoodKey2Desks = SeatingPlanModel.hoodDesks(hoods, desks, deskAssignments);

        return hoods.map<HoodItem>(hood => {
            const hoodEmployees = hoodKey2Employees.get(hood.key);
            const hoodDesks = hoodKey2Desks.get(hood.key);
            return {
                ...hood,
                oldEmployees: hoodEmployees?.oldEmployees || [],
                newEmployees: hoodEmployees?.newEmployees || [],
                oldDesks: hoodDesks?.oldDesks || [],
                newDesks: hoodDesks?.newDesks || [],
            };
        });
    }

    static createModel(
        plan: SeatingPlan,
        existingNeighbourhoods: Neighbourhood[],
        desks: Desk[],
        floors: Floor[],
        employeeVisits: EmployeeVisitSheet,
        store: SeatingPlanStore,
    ) {
        const hoodItems = SeatingPlanModel.buildHoodItems(plan, existingNeighbourhoods, desks);
        const deskAssignments = SeatingPlanModel.deskAssignments(desks, hoodItems, plan.deskAssignments);
        const assignedDesks = new AssignedDesks(desks, desk => {
            const assignment = deskAssignments.get(desk.id);
            const actualAssignment = assignment && (assignment.new || assignment.old);
            return actualAssignment?.employee;
        });
        const hoodKey2employeeId2move = mapValues(
            groupBy(
                plan.moves,
                move => SeatingPlanModel.hoodRefKey(move.neighbourhood)
            ),
            moves => associateBy(moves, move => move.employee.id)
        );
        const unassignedHood = hoodItems.find(hood => hood.key === UNASSIGNED_NEIGHBOURHOOD_KEY);
        if (unassignedHood) {
            const oldEmployees = associateBy(unassignedHood.oldEmployees, employee => employee.id);
            const newEmployees = associateBy(unassignedHood.newEmployees, employee => employee.id);
            const unassignedHoodMoves = [
                ...unassignedHood.oldEmployees
                    .filter(employee => !newEmployees.has(employee.id))
                    .map<SeatingPlanMove>(employee => ({
                        employee,
                        neighbourhood: SeatingPlanModel.hoodRef(unassignedHood),
                        operation: 'remove',
                    })),
                ...unassignedHood.newEmployees
                    .filter(employee => !oldEmployees.has(employee.id))
                    .map<SeatingPlanMove>(employee => ({
                        employee,
                        neighbourhood: SeatingPlanModel.hoodRef(unassignedHood),
                        operation: 'add',
                    })),
            ];
            if (unassignedHoodMoves.length > 0) {
                hoodKey2employeeId2move.set(UNASSIGNED_NEIGHBOURHOOD_KEY, associateBy(unassignedHoodMoves, move => move.employee.id));
            }
        }
        const employeeId2moves = groupBy(
            plan.moves,
            move => move.employee.id
        );

        const originalEmployeeNeighbourhoods = new Map<Employee['id'], HoodItem[]>();
        const modifiedEmployeeNeighbourhoods = new Map<Employee['id'], HoodItem[]>();
        hoodItems.forEach(hood => {
            hood.oldEmployees.forEach(employee => {
                getOrSet(originalEmployeeNeighbourhoods, employee.id, emptyArray).push(hood);
            });
            hood.newEmployees.forEach(employee => {
                getOrSet(modifiedEmployeeNeighbourhoods, employee.id, emptyArray).push(hood);
            });
        })
        const originalEmployeeNeighbourhood = new EmployeeNeighbourhood(originalEmployeeNeighbourhoods);
        const modifiedEmployeeNeighbourhood = new EmployeeNeighbourhood(modifiedEmployeeNeighbourhoods);

        return new SeatingPlanModel(
            plan,
            hoodItems,
            hoodItems.filter(hood => hood.key !== UNASSIGNED_NEIGHBOURHOOD_KEY),
            desks,
            floors,
            employeeVisits,
            assignedDesks,
            deskAssignments,
            hoodKey2employeeId2move,
            employeeId2moves,
            originalEmployeeNeighbourhood,
            modifiedEmployeeNeighbourhood,
            store,
        );
    }
}
